import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetBranchApiTokenResponse } from '@/api/handlers/resources/branches/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { BranchQueryKeys } from './branchQueryKeys';

/**
 * Reads the active branch API token via `GET /branches/{id}/api-token`
 * (BOS-77/79). The query is callsite-gated via `enabled` — the enrollment
 * modal only fires it when it actually opens for a branch that has a
 * token, so we don't pull plaintext for every row in the table.
 *
 * Stale time matches the details query: the token only changes via the
 * generate/revoke mutations we own, which patch this cache directly on
 * success, so no time-based refetch is needed.
 */
export const useBranchApiTokenQuery = (id: string, enabled: boolean) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetBranchApiTokenResponse>>({
        queryKey: BranchQueryKeys.apiToken(id),
        queryFn: () => apiClient.branches.getApiToken(id),
        enabled,
        staleTime: 60_000,
        // The cache is only invalidated by our own POST/DELETE mutations
        // (which `setQueryData` directly). A tab refocus has no
        // legitimate reason to refetch the plaintext, so suppress the
        // default to avoid pulling sensitive bytes over the wire on
        // every focus event.
        refetchOnWindowFocus: false,
    });
};
